// frontend/src/hooks/patient/usePatientSummaryComments.js
//
// usePatientSummaryComments(appointmentId, role)
//   -> { comments, addComment, resolveComment, markCommentsSeen, unseenCount }
//
// CRUD + realtime for the patient-facing visit-summary comment tool, backed by
// `public.patient_summary_comments` (a clone of the clinician `document_comments`
// store). Both patients and clinicians read/write through the SAME public client
// (`supabasePublic()`), so they share one store and see each other's comments.
//
// Each comment carries a `target` ('document' or 'audio:<n>') so a comment can be
// attached to the document summary OR a specific recording summary, and a
// `doctor_seen` flag that drives the doctor's unseen-comment notification badge.
//
// IMPORTANT: `addComment` deliberately has NO `patientReadOnly` guard — patients
// ARE allowed to comment on their own visit summary. (The clinician document
// comment tool blocks patient writes; this one does not.)
import { useCallback, useEffect, useMemo, useState } from "react";
import { supabasePublic } from "../../components/supabaseClient";
import { uniqueChannelTopic } from "../../utils/realtimeChannel";

const TABLE = "patient_summary_comments";

// NOTE: this hook's realtime channel MUST use a per-subscriber topic (see
// uniqueChannelTopic). A fixed `patient_summary_comments:<appointmentId>` topic
// crashed production two ways:
//   1. Two mounts for the SAME appointment — the clinic page's unseen-count
//      badge and the summary panel both call this hook with the same id.
//   2. A remount before cleanup finished — removeChannel() is async, so
//      navigating between patient pages re-entered this effect while the old
//      channel was still registered.
// Both returned an already-subscribed channel, and .on('postgres_changes', …)
// then threw "…after subscribe()" — uncaught, taking down the render tree.

export function usePatientSummaryComments(appointmentId, role) {
  const [comments, setComments] = useState([]);
  const isClinician = !!role && role !== "patient";

  const fetchComments = useCallback(async () => {
    if (!appointmentId) return;
    const { data } = await supabasePublic()
      .from(TABLE)
      .select("*")
      .eq("appointment_id", appointmentId)
      .order("created_at", { ascending: true });
    setComments(data || []);
  }, [appointmentId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (!appointmentId) return undefined;
    const client = supabasePublic();
    let channel;
    try {
      channel = client
        // Per-subscriber topic. Never use a bare `table:appointmentId` here.
        .channel(uniqueChannelTopic(`patient_summary_comments:${appointmentId}`))
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: TABLE,
            filter: `appointment_id=eq.${appointmentId}`,
          },
          () => fetchComments()
        )
        .subscribe();
    } catch (err) {
      // Live updates are a nicety — comments still load via fetchComments() and
      // refresh after every write. Never let a realtime failure crash the page
      // (this threw an *uncaught* error that took down the whole render tree).
      console.warn("[patient-summary-comments] realtime unavailable:", err?.message || err);
      return undefined;
    }
    return () => {
      try {
        client.removeChannel(channel);
      } catch (err) {
        console.warn("[patient-summary-comments] channel cleanup failed:", err?.message || err);
      }
    };
  }, [appointmentId, fetchComments]);

  const addComment = useCallback(
    async ({ content, highlightText, selectionRange, color, user, target }) => {
      // NOTE: no patientReadOnly guard — patients are allowed to comment here.
      if (!appointmentId) return;
      const base = {
        appointment_id: appointmentId,
        user_id: user?.id,
        user_name: user?.name || user?.email || "Anonymous",
        user_email: user?.email,
        content,
        highlight_text: highlightText,
        selection_range: selectionRange,
        highlight_color: color || "#FEF08A",
        status: "open",
      };
      const withExtras = {
        ...base,
        target: target || "document",
        // A clinician's own comment is already "seen"; a patient's is not.
        doctor_seen: isClinician,
      };
      const res = await supabasePublic().from(TABLE).insert([withExtras]);
      // If the target/doctor_seen migration isn't applied (or PostgREST's schema
      // cache is stale), the column is unknown — fall back to the base columns so
      // comments still save. Targeting + seen-tracking come online once it lands.
      if (res?.error && /(target|doctor_seen)/i.test(res.error.message || "")) {
        await supabasePublic().from(TABLE).insert([base]);
      }
      fetchComments();
    },
    [appointmentId, isClinician, fetchComments]
  );

  const resolveComment = useCallback(
    async (id) => {
      await supabasePublic().from(TABLE).update({ status: "resolved" }).eq("id", id);
      fetchComments();
    },
    [fetchComments]
  );

  // Mark every unseen comment on this appointment as seen by the doctor. Called
  // when the clinician opens the summary/comments view — clears the badge.
  const markCommentsSeen = useCallback(async () => {
    if (!appointmentId) return;
    await supabasePublic()
      .from(TABLE)
      .update({ doctor_seen: true })
      .eq("appointment_id", appointmentId)
      .eq("doctor_seen", false);
    fetchComments();
  }, [appointmentId, fetchComments]);

  // Strict `=== false` so that before the migration (column absent → undefined)
  // the badge stays at 0 instead of counting every comment as unseen.
  const unseenCount = useMemo(
    () => comments.filter((c) => c.doctor_seen === false).length,
    [comments]
  );

  return { comments, addComment, resolveComment, markCommentsSeen, unseenCount };
}
